import React from 'react';
import { Form, Select } from 'antd';
import type { FormInstance, SelectProps } from 'antd';
import { FONT_FAMILY_OPTIONS, type FontOption } from '../../utils/appFonts';
import { hasFontFamilyValue, isBoldFromFontFamily, resolveFontFamily } from '../../utils/fontFamily';
import { styleFieldName } from '../../utils/styleFormFieldNames';

type Props = SelectProps;

type FormSyncProps = {
  form: FormInstance;
  namePrefix?: string | string[];
};

function syncBoldFieldFromFontFamily(form: FormInstance, namePrefix: string | string[] | undefined, fontFamily: unknown) {
  const boldFromFont = isBoldFromFontFamily(fontFamily);
  if (boldFromFont == null) return;
  form.setFieldValue(styleFieldName(namePrefix, 'bold'), boldFromFont);
  form.setFieldValue(styleFieldName(namePrefix, 'fontWeight'), undefined);
}

const FontFamilySelectControl = React.forwardRef<any, Props>(function FontFamilySelectControl(
  { className, style, ...rest },
  ref,
) {
  const value = rest.value;
  const hasFont = hasFontFamilyValue(value);
  const resolved = hasFont ? resolveFontFamily(String(value)) : undefined;

  return (
    <Select
      ref={ref}
      size="small"
      allowClear
      placeholder="Font"
      className={['word-ribbon-font-select', hasFont ? 'word-ribbon-font-select--active' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={{
        ...style,
        ...(resolved ? { fontFamily: resolved } : {}),
      }}
      popupMatchSelectWidth={false}
      options={FONT_FAMILY_OPTIONS.map((opt: FontOption) => ({
        ...opt,
        label: (
          <span style={opt.value ? { fontFamily: resolveFontFamily(String(opt.value)) } : undefined}>
            {opt.label}
          </span>
        ),
      }))}
      {...rest}
    />
  );
});

export const FontFamilySelect = FontFamilySelectControl;

const FontFamilySelectWithFormSync = React.forwardRef<
  any,
  FormSyncProps & Pick<SelectProps, 'value' | 'onChange' | 'className' | 'style'>
>(function FontFamilySelectWithFormSync({ form, namePrefix, onChange, ...rest }, ref) {
  return (
    <FontFamilySelectControl
      ref={ref}
      {...rest}
      onChange={(value) => {
        onChange?.(value);
        syncBoldFieldFromFontFamily(form, namePrefix, value);
      }}
    />
  );
});

/** Font family picker that keeps the bold toggle in sync for Vag / Vag-Bold. */
export const FontFamilyFormSelect: React.FC<FormSyncProps> = ({ form, namePrefix }) => (
  <Form.Item name={styleFieldName(namePrefix, 'fontFamily')} noStyle>
    <FontFamilySelectWithFormSync form={form} namePrefix={namePrefix} />
  </Form.Item>
);
